import React from "react";
import { Check, CheckCheck, AlertCircle, Download, Loader2 } from "lucide-react";
import { formatTimestampForDisplay } from "../../utils/timestampUtils";

/**
 * MessageItem Component - Displays a single message with media support
 */
const MessageItem = ({ message, downloadingMedia, onDownloadMedia }) => {
  /**
   * Get message status icon
   * Status progression: sent (1 grey tick) -> delivered (2 grey ticks) -> read (2 blue ticks)
   * Failed shows red icon with error message
   */
  const getStatusIcon = (message) => {
    if (message.direction === "inbound") return null;
    
    const status = message.message_status || "sent";
    const statuses = message.statuses || [];
    
    // Check for failed status (highest priority)
    const hasFailed = status === "failed" || statuses.some((s) => s.status === "failed");
    const errorMessage = message.error_details || message.error_message || message.error_title || message.error_code;

    if (hasFailed) {
      return (
        <div className="relative group">
          <AlertCircle size={12} className="text-red-500" />
          {errorMessage && (
            <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              {errorMessage}
              {message.error_code && (
                <div className="text-[10px] text-gray-400 mt-1">
                  Code: {message.error_code}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Check for read status (two blue ticks)
    const hasRead = status === "read" || statuses.some((s) => s.status === "read");
    if (hasRead) {
      return <CheckCheck size={12} className="text-blue-500" />;
    }
    
    // Check for delivered status (two grey ticks)
    const hasDelivered = status === "delivered" || statuses.some((s) => s.status === "delivered");
    if (hasDelivered) {
      return <CheckCheck size={12} className="text-gray-400" />;
    }
    
    // Default: sent status (one grey tick)
    if (status === "sent") {
      return <Check size={12} className="text-gray-400" />;
    }
    
    return null;
  };

  /**
   * Handle media load completion (called from onLoad/onError handlers)
   * Clears the downloading state when media finishes loading
   */
  const handleMediaLoadComplete = () => {
    if (onDownloadMedia) {
      onDownloadMedia(message, true);
    }
  };

  /**
   * Render media content based on message type
   */
  const renderMedia = () => {
    if (!message.media_url && !message.media_id) return null;
    if (message.message_type === "text" || message.message_type === "button") return null;

    const isDownloading = downloadingMedia.has(message.message_id);
    const hasDownloadedUrl = !!message.downloaded_media_url;

    // Image messages
    if (message.message_type === "image" || message.mime_type?.startsWith("image/")) {
      if (hasDownloadedUrl) {
        return (
          <div className="relative">
            <img
              key={`img-${message.message_id}-${message.downloaded_media_url}`}
              src={message.downloaded_media_url}
              alt="Image"
              className="max-w-full max-h-64 rounded object-cover w-full"
              onLoad={handleMediaLoadComplete}
              onError={(e) => {
                console.error("Error loading downloaded image:", e);
                e.target.style.display = "none";
                handleMediaLoadComplete();
              }}
            />
            {isDownloading && (
              <div className="absolute inset-0 bg-black/30 rounded flex items-center justify-center z-10">
                <div className="bg-white/90 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-primary" />
                  <span className="text-xs text-gray-700">Processing...</span>
                </div>
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div className="relative bg-gray-200 rounded flex items-center justify-center min-h-[200px]">
            {!hasDownloadedUrl && (
              <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center z-10 group-hover:bg-black/50 transition-colors">
                <button
                  onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
                  disabled={isDownloading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg font-medium transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                    message.direction === "outbound"
                      ? "bg-white text-primary hover:bg-gray-50"
                      : "bg-primary text-white hover:bg-primary/90"
                  }`}
                  title={message.media_id && !message.media_url ? "Download media from Meta" : "Download media to view"}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span className="text-sm">Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      <span className="text-sm">Download to View</span>
                    </>
                  )}
                </button>
              </div>
            )}
            <div className="text-center">
              <div className="text-4xl mb-2">📷</div>
              <p className="text-xs text-gray-500">
                {message.media_id && !message.media_url 
                  ? "Click to download from Meta" 
                  : "Image preview unavailable"}
              </p>
            </div>
          </div>
        );
      }
    }

    // Video messages
    if (message.message_type === "video" || message.mime_type?.startsWith("video/")) {
      if (hasDownloadedUrl || message.media_url) {
        return (
          <video
            key={message.downloaded_media_url || message.media_url}
            src={message.downloaded_media_url || message.media_url}
            controls
            className="max-w-full max-h-64 rounded w-full"
            onLoadedData={hasDownloadedUrl ? handleMediaLoadComplete : undefined}
          >
            Your browser does not support the video tag.
          </video>
        );
      } else {
        return (
          <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">🎥</div>
            <p className="text-sm font-medium text-gray-700 mb-3">Video Available</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        );
      }
    }

    // Audio messages
    if (message.message_type === "audio" || message.mime_type?.startsWith("audio/")) {
      if (hasDownloadedUrl || message.media_url) {
        return (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-2xl">🎵</span>
            <audio 
              key={message.downloaded_media_url || message.media_url}
              src={message.downloaded_media_url || message.media_url} 
              controls 
              className="flex-1" 
            />
          </div>
        );
      } else {
        return (
          <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">🎵</div>
            <p className="text-sm font-medium text-gray-700 mb-3">Audio Available</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        );
      }
    }

    // Document messages
    if (message.message_type === "document" || message.mime_type) {
      return (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <span className="text-3xl">📄</span>
          <div className="flex-1">
            {hasDownloadedUrl ? (
              <a
                href={message.downloaded_media_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline break-all block"
              >
                {message.message_text || "Document"}
              </a>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{message.message_text || "Document"}</p>
                <button
                  onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
                  disabled={isDownloading}
                  className="px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      <span>Download</span>
                    </>
                  )}
                </button>
              </div>
            )}
            {message.file_size && (
              <p className="text-xs text-gray-500 mt-1">
                {(message.file_size / 1024).toFixed(2)} KB
              </p>
            )}
          </div>
        </div>
      );
    }

    // Generic media fallback
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        {hasDownloadedUrl ? (
          <a
            href={message.downloaded_media_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
          >
            <span className="text-xl">📎</span>
            <span>View Media</span>
          </a>
        ) : (
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700 mb-3">Media file</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex ${
        message.direction === "outbound" ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`max-w-[70%] rounded-lg px-3 py-2 shadow-sm ${
          message.direction === "outbound"
            ? "bg-primary text-white"
            : "bg-white text-gray-900"
        }`}
      >
        {/* Button Message Display */}
        {(message.message_type === "button" || message.message_type === "interactive") && (
          <div className="mb-2">
            {message.button?.text ? (
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                message.direction === "outbound" 
                  ? "bg-white/20 border border-white/30 text-white" 
                  : "bg-gray-100 border border-gray-200 text-gray-700"
              }`}>
                <span className="text-xs font-medium opacity-80">🔘 Button Clicked:</span>
                <span className="text-sm font-semibold">{message.button.text}</span>
              </div>
            ) : message.button?.payload ? (
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                message.direction === "outbound" 
                  ? "bg-white/20 border border-white/30 text-white" 
                  : "bg-gray-100 border border-gray-200 text-gray-700"
              }`}>
                <span className="text-xs font-medium opacity-80">🔘 Button:</span>
                <span className="text-sm font-semibold">{message.button.payload}</span>
              </div>
            ) : message.message_text ? (
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                message.direction === "outbound" 
                  ? "bg-white/20 border border-white/30 text-white" 
                  : "bg-gray-100 border border-gray-200 text-gray-700"
              }`}>
                <span className="text-xs font-medium opacity-80">🔘</span>
                <span className="text-sm font-semibold">{message.message_text}</span>
              </div>
            ) : (
              <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                message.direction === "outbound" 
                  ? "bg-white/20 border border-white/30 text-white" 
                  : "bg-gray-100 border border-gray-200 text-gray-700"
              }`}>
                <span className="text-xs font-medium opacity-80">🔘 Button Interaction</span>
              </div>
            )}
          </div>
        )}
        
        {/* Media Messages */}
        {(message.media_url || (message.media_id && message.message_type !== "text" && message.message_type !== "button" && message.message_type !== "interactive")) && (
          <div className="mb-2 relative group">
            {renderMedia()}
          </div>
        )}

        {/* Caption for media messages (from message.caption or message.message_text) */}
        {message.caption && ["image", "video", "audio", "document"].includes(message.message_type) && (
          <p className="text-sm whitespace-pre-wrap break-words mt-2">
            {message.caption}
          </p>
        )}

        {/* Message Text (for text messages only, not button or media with caption) */}
        {message.message_text && 
         message.message_type === "text" && 
         !message.caption && (
          <p className="text-sm whitespace-pre-wrap break-words">
            {message.message_text}
          </p>
        )}

        {/* Timestamp and Status */}
        <div
          className={`flex items-center gap-1 mt-1 ${
            message.direction === "outbound"
              ? "justify-end"
              : "justify-start"
          }`}
        >
          <span
            className={`text-[10px] ${
              message.direction === "outbound"
                ? "text-white/80"
                : "text-gray-500"
            }`}
          >
            {formatTimestampForDisplay(message.sent_at)}
          </span>
          {getStatusIcon(message)}
        </div>
      </div>
    </div>
  );
};

export default MessageItem;

